/**
 * The rules of an obsolescence case as the screen applies them. The server enforces the same
 * rules and answers 409 when a move is refused; these only say why a button is off before anyone
 * presses it. Mirrors `apps/api/app/api/v1/cases.py`.
 */
import type { Schemas } from "@neio/contracts";
import { ApiError, switchedOff } from "../../lib/http";

export type Case = Schemas["CaseView"];
export type Outcome = Schemas["NewResolution"]["outcome"];
export type CaseState = "open" | "resolution_selected" | "funded" | "closed";

export const STATES: CaseState[] = ["open", "resolution_selected", "funded", "closed"];

export const STATE_LABELS: Record<CaseState, string> = {
  open: "Open",
  resolution_selected: "Resolution selected",
  funded: "Funded",
  closed: "Closed",
};

/** The button that moves a case into each state. */
export const MOVE_LABELS: Record<CaseState, string> = {
  open: "Reopen",
  resolution_selected: "Select resolution",
  funded: "Mark funded",
  closed: "Close case",
};

/** SD-22 Table 9 resolutions, in the API's order. Typed against the contract, so a new value
 * on the server fails the typecheck here until it has a label. */
export const OUTCOME_LABELS: Record<Outcome, string> = {
  approved_item: "Approved item",
  life_of_need_buy: "Life-of-need buy",
  repair_or_reclamation: "Repair or reclamation",
  extension_of_production: "Extension of production",
  simple_substitute: "Simple substitute",
  complex_substitute: "Complex substitute",
  new_item_or_source: "New item or source",
  next_higher_assembly_redesign: "Next higher assembly redesign",
  system_redesign: "System redesign",
};

export const OUTCOMES = Object.keys(OUTCOME_LABELS) as Outcome[];

/** A resolution that buys time rather than solving the problem, so a later one may follow. */
const BRIDGES: string[] = ["life_of_need_buy"];

export function outcomeLabel(outcome: unknown): string {
  return OUTCOME_LABELS[outcome as Outcome] ?? String(outcome ?? "").replace(/_/g, " ");
}

export function stateLabel(state: string): string {
  return STATE_LABELS[state as CaseState] ?? state.replace(/_/g, " ");
}

export function nextState(state: string): CaseState | null {
  const i = STATES.indexOf(state as CaseState);
  return i >= 0 && i < STATES.length - 1 ? STATES[i + 1] : null;
}

/** Why the case cannot move to its next state, or null if it can. */
export function advanceBlock(c: Case): string | null {
  const next = nextState(c.state);
  if (!next) return "The case is closed.";
  if (next === "resolution_selected" && c.resolutions.length === 0) {
    return "Record a resolution first. Recording one selects it.";
  }
  return null;
}

/** Why no resolution can be recorded now, or null if one can. */
export function resolveBlock(c: Case): string | null {
  if (c.state === "closed") return "The case is closed.";
  if (c.resolutions.some((r) => !BRIDGES.includes(String(r.outcome)))) {
    return "A resolution is recorded. Only a life-of-need buy, which buys time, takes a later one.";
  }
  return null;
}

export type CaseFailure = "off" | "missing" | "forbidden" | "error";

/**
 * How a failed case read should be shown. Both "switched off" and "no such case" are 404, so only
 * the server's "not enabled" words make it off (`switchedOff` with no bare statuses).
 */
export function caseFailure(error: unknown): CaseFailure {
  if (!(error instanceof ApiError)) return "error";
  if (error.status === 404) return switchedOff(error, []) !== null ? "off" : "missing";
  if (error.status === 403) return "forbidden";
  return "error";
}
